// Visor del catálogo con fotos de un proveedor de alquiler (modal de la tarjeta
// de alquiler de la ficha del presupuesto).
//
// - Pestañas por documento (catálogo general, mantelería, sillas...).
// - Izquierda: la página (ajustada al alto; al pulsar, a tamaño real con
//   scroll), Anterior / Siguiente y, en el catálogo general, salto a sección.
// - Derecha: los artículos de la página con cantidad y "Añadir" (la misma
//   función de añadir línea que el buscador). En las hojas de fotos, los
//   modelos rotulados con sus materiales enlazados.
// - El manifest se pide la primera vez que se abre y queda en memoria.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { catalogosAlquilerAPI } from '../../api/api';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';

const claseBoton = 'h-[34px] px-3 rounded-lg border text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;
const botonSecundario = `${claseBoton} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`;
const claseInput = 'px-2 py-1.5 text-sm border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary disabled:bg-gray-50 disabled:text-gray-500';

const precio = (n) => (Number(n) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

const numero = (texto) => {
  const n = parseFloat(String(texto ?? '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const formatFecha = (valor) => {
  const t = String(valor || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
  return `${t.slice(8, 10)}/${t.slice(5, 7)}/${t.slice(0, 4)}`;
};

// "Alquilado N veces" / "Sin alquilar" (null si no hay datos de uso)
export const textoUsoAlquiler = (uso, materialId) => {
  if (!uso || !materialId) return null;
  const u = uso[materialId];
  const n = Number(u?.eventos) || 0;
  if (n <= 0) return { texto: 'Sin alquilar', titulo: 'No se ha alquilado en ningún evento confirmado' };
  const detalle = [
    u.unidades ? `${Number(u.unidades).toLocaleString('es-ES')} unidades en total` : null,
    u.ultimo ? `último el ${formatFecha(u.ultimo)}${u.ultimo_cliente ? ` (${u.ultimo_cliente})` : ''}` : null,
  ].filter(Boolean).join(' · ');
  return { texto: `Alquilado ${n} ${n === 1 ? 'vez' : 'veces'}`, titulo: detalle || undefined };
};

const mensajeError = (err, porDefecto) => {
  const detalle = err?.response?.data?.detail;
  return typeof detalle === 'string' ? detalle : porDefecto;
};

const FilaArticulo = ({ art, enLista, cantidadTexto, onCantidad, onAnadir, ocupado, bloqueado, uso }) => {
  const infoUso = textoUsoAlquiler(uso, art.material_id);
  const lavado = art.lavado_aparte || Number(art.coste_limpieza) > 0;
  return (
    <li className="flex items-center justify-between gap-2 px-2.5 py-2" data-testid={`catalogo-articulo-${art.material_id || art.nombre}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#16221D]">
          {art.nombre}
          {art.aproximado && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-[#FDF0DC] text-[10px] font-semibold text-[#7A3A06] align-middle">parecido</span>}
        </p>
        <p className="text-xs text-[#5B6B64]">
          {[
            art.medida,
            art.precio !== null && art.precio !== undefined ? `${precio(art.precio)} €/día` : null,
            lavado ? `lavado ${precio(art.coste_limpieza)}` : null,
          ].filter(Boolean).join(' · ')}
        </p>
        {infoUso && (
          <p className={`text-xs ${infoUso.texto === 'Sin alquilar' ? 'text-[#8A958F]' : 'text-[#0F5E43]'}`} title={infoUso.titulo}>
            {infoUso.texto}
          </p>
        )}
      </div>
      {!art.material_id ? (
        <span className="text-xs text-[#8A958F] whitespace-nowrap">No está en el ERP</span>
      ) : (
        <div className="flex items-center gap-1.5 shrink-0">
          {enLista !== null && (
            <span className="text-xs font-semibold text-[#0F5E43] whitespace-nowrap" title="Ya está en la lista del alquiler">
              En la lista ({Number(enLista).toLocaleString('es-ES')})
            </span>
          )}
          <input
            type="number"
            min="1"
            step="1"
            value={cantidadTexto}
            onChange={(e) => onCantidad(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onAnadir(); }}
            disabled={bloqueado}
            className={`${claseInput} w-16 text-right tabular-nums`}
            aria-label={`Cantidad de ${art.nombre}`}
          />
          <button
            type="button"
            onClick={onAnadir}
            disabled={ocupado || bloqueado}
            className={enLista !== null ? botonSecundario : botonPrincipal}
            data-testid={`catalogo-anadir-${art.material_id}`}
          >
            {enLista !== null ? 'Sumar' : 'Añadir'}
          </button>
        </div>
      )}
    </li>
  );
};

const CatalogoFotosAlquiler = ({ abierto, onCerrar, catalogos, cantidadesEnLista, onAnadir, uso, aviso, bloqueado }) => {
  const [claveCatalogo, setClaveCatalogo] = useState(null);
  const [manifests, setManifests] = useState({}); // { clave: manifest }
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [docActivo, setDocActivo] = useState(null);
  const [paginas, setPaginas] = useState({}); // { clave_doc: índice }
  const [zoom, setZoom] = useState(false);
  const [cantidades, setCantidades] = useState({}); // { material_id: texto }
  const [anadiendo, setAnadiendo] = useState(null);

  const clave = claveCatalogo || catalogos?.[0]?.clave_catalogo || null;
  const manifest = clave ? manifests[clave] : null;

  // Carga perezosa: la primera vez que se abre (y por catálogo)
  useEffect(() => {
    if (!abierto || !clave || manifests[clave]) return undefined;
    let cancelado = false;
    setCargando(true);
    setError(null);
    catalogosAlquilerAPI.manifest(clave)
      .then(({ data }) => {
        if (cancelado) return;
        if (!data || !Array.isArray(data.documentos)) {
          setError('El catálogo no tiene un formato válido.');
          return;
        }
        setManifests(prev => ({ ...prev, [clave]: data }));
      })
      .catch((err) => { if (!cancelado) setError(mensajeError(err, 'No se pudo cargar el catálogo.')); })
      .finally(() => setCargando(false));
    return () => { cancelado = true; };
  }, [abierto, clave, manifests]);

  const documentos = useMemo(() => (manifest?.documentos || []).filter(d => (d.paginas || []).length > 0), [manifest]);
  const doc = documentos.find(d => d.clave === docActivo) || documentos[0] || null;
  const totalPaginas = doc ? doc.paginas.length : 0;
  const indice = doc ? Math.min(paginas[doc.clave] || 0, Math.max(0, totalPaginas - 1)) : 0;
  const pagina = doc ? doc.paginas[indice] : null;

  // Secciones del documento: primera página de cada una (orden de aparición)
  const secciones = useMemo(() => {
    const res = [];
    (doc?.paginas || []).forEach((p, i) => {
      (p.secciones || []).forEach((s) => { if (!res.some(x => x.seccion === s)) res.push({ seccion: s, indice: i }); });
    });
    return res;
  }, [doc]);

  const irA = useCallback((i) => {
    if (!doc) return;
    const destino = Math.max(0, Math.min(doc.paginas.length - 1, i));
    setPaginas(prev => ({ ...prev, [doc.clave]: destino }));
    setZoom(false);
  }, [doc]);

  const alTeclado = (e) => {
    const etiqueta = e.target?.tagName;
    if (etiqueta === 'INPUT' || etiqueta === 'SELECT' || etiqueta === 'TEXTAREA') return;
    if (e.key === 'ArrowRight') { e.preventDefault(); irA(indice + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); irA(indice - 1); }
  };

  const anadir = async (art) => {
    const n = numero(cantidades[art.material_id] ?? '1');
    if (n === null || n <= 0 || !art.material_id) return;
    setAnadiendo(art.material_id);
    try {
      const data = await onAnadir(art.material_id, art.nombre, n);
      if (data) setCantidades(prev => { const r = { ...prev }; delete r[art.material_id]; return r; });
    } finally {
      setAnadiendo(null);
    }
  };

  const fila = (art, clavePrefijo = '') => {
    const enLista = art.material_id && cantidadesEnLista && cantidadesEnLista[art.material_id] !== undefined
      ? cantidadesEnLista[art.material_id] : null;
    return (
      <FilaArticulo
        key={`${clavePrefijo}${art.material_id || art.nombre}`}
        art={art}
        enLista={enLista}
        cantidadTexto={cantidades[art.material_id] ?? '1'}
        onCantidad={(v) => setCantidades(prev => ({ ...prev, [art.material_id]: v }))}
        onAnadir={() => anadir(art)}
        ocupado={anadiendo === art.material_id}
        bloqueado={bloqueado}
        uso={uso}
      />
    );
  };

  const tieneModelos = !!(pagina && Array.isArray(pagina.modelos));
  const catalogoActual = (catalogos || []).find(c => c.clave_catalogo === clave);
  const urlImagen = pagina && clave ? catalogosAlquilerAPI.paginaUrl(clave, pagina.imagen) : null;

  return (
    <Dialog open={abierto} onOpenChange={(v) => { if (!v) onCerrar(); }}>
      <DialogContent
        className="max-w-[97vw] w-[97vw] h-[94vh] p-0 gap-0 flex flex-col overflow-hidden"
        onKeyDown={alTeclado}
        data-testid="catalogo-fotos"
      >
        {/* Cabecera */}
        <div className="flex flex-wrap items-center gap-3 border-b border-[#E1E6E3] px-4 py-3 pr-12">
          <div className="min-w-0 mr-auto">
            <DialogTitle className="text-base font-bold text-[#16221D]">
              Catálogo {catalogoActual?.proveedor || manifest?.proveedor || ''}{catalogoActual?.tarifa ? ` ${catalogoActual.tarifa}` : ''}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5B6B64]">
              Añade artículos a la lista del alquiler sin cerrar el catálogo. Pulsa la página para verla a tamaño real.
            </DialogDescription>
          </div>
          {(catalogos || []).length > 1 && (
            <select
              value={clave || ''}
              onChange={(e) => { setClaveCatalogo(e.target.value); setDocActivo(null); setZoom(false); }}
              className={`${claseInput} w-56`}
              aria-label="Catálogo"
            >
              {catalogos.map(c => (
                <option key={c.clave_catalogo} value={c.clave_catalogo}>{`${c.proveedor} ${c.tarifa}`.trim()}</option>
              ))}
            </select>
          )}
          <button type="button" onClick={onCerrar} className={botonSecundario} data-testid="catalogo-cerrar">Cerrar</button>
        </div>

        {/* Pestañas por documento */}
        {documentos.length > 0 && (
          <div className="flex gap-1 overflow-x-auto border-b border-[#E1E6E3] px-4 pt-2" role="tablist">
            {documentos.map((d) => {
              const activo = doc && d.clave === doc.clave;
              return (
                <button
                  key={d.clave}
                  type="button"
                  role="tab"
                  aria-selected={activo}
                  onClick={() => { setDocActivo(d.clave); setZoom(false); }}
                  className={`px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${activo ? 'border-duetto-primary text-[#0F5E43]' : 'border-transparent text-[#5B6B64] hover:text-[#16221D]'}`}
                  data-testid={`catalogo-doc-${d.clave}`}
                >
                  {d.titulo || d.clave}
                </button>
              );
            })}
          </div>
        )}

        {cargando && !manifest && <p className="p-6 text-sm text-[#5B6B64]">Cargando el catálogo...</p>}
        {error && !manifest && <p className="p-6 text-sm text-[#8A1C12]">{error}</p>}

        {doc && pagina && (
          <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
            {/* Página */}
            <div className="flex-1 min-h-0 min-w-0 flex flex-col bg-[#F3F5F4]">
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-[#E1E6E3] bg-white">
                <button type="button" onClick={() => irA(indice - 1)} disabled={indice <= 0} className={botonSecundario} data-testid="catalogo-anterior">
                  Anterior
                </button>
                <span className="text-sm tabular-nums text-[#16221D]" data-testid="catalogo-numero-pagina">
                  Página {indice + 1} de {totalPaginas}
                </span>
                <button type="button" onClick={() => irA(indice + 1)} disabled={indice >= totalPaginas - 1} className={botonSecundario} data-testid="catalogo-siguiente">
                  Siguiente
                </button>
                {secciones.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value !== '') irA(Number(e.target.value)); }}
                    className={`${claseInput} w-48 ml-auto`}
                    aria-label="Ir a la sección"
                    data-testid="catalogo-seccion"
                  >
                    <option value="">Ir a la sección...</option>
                    {secciones.map(s => <option key={s.seccion} value={s.indice}>{s.seccion}</option>)}
                  </select>
                )}
              </div>
              <div className={`flex-1 min-h-0 ${zoom ? 'overflow-auto' : 'overflow-hidden flex items-center justify-center p-2'}`}>
                {urlImagen && (
                  <img
                    key={urlImagen}
                    src={urlImagen}
                    alt={`${doc.titulo || 'Catálogo'}, página ${indice + 1}`}
                    width={pagina.ancho || undefined}
                    height={pagina.alto || undefined}
                    onClick={() => setZoom(z => !z)}
                    className={zoom ? 'max-w-none cursor-zoom-out' : 'max-h-full max-w-full w-auto h-auto object-contain cursor-zoom-in shadow'}
                    data-testid="catalogo-imagen"
                  />
                )}
              </div>
            </div>

            {/* Artículos de la página */}
            <div className="lg:w-[420px] shrink-0 min-h-0 max-h-[40vh] lg:max-h-none flex flex-col border-t lg:border-t-0 lg:border-l border-[#E1E6E3] bg-white">
              <div className="px-3 py-2 border-b border-[#E1E6E3]">
                <p className="text-xs font-bold tracking-wider uppercase text-[#5B6B64]">
                  {tieneModelos ? 'Modelos de la foto' : 'Artículos de la página'}
                </p>
                {(pagina.secciones || []).length > 0 && (
                  <p className="text-xs text-[#5B6B64]">{pagina.secciones.join(' · ')}</p>
                )}
                {aviso && (
                  <p className={`text-xs mt-1 ${aviso.tipo === 'ok' ? 'text-[#0F5E43]' : aviso.tipo === 'info' ? 'text-[#7A3A06]' : 'text-[#8A1C12]'}`} data-testid="catalogo-mensaje">
                    {aviso.texto}
                  </p>
                )}
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto" data-testid="catalogo-articulos">
                {tieneModelos ? (
                  pagina.modelos.length === 0 ? (
                    <p className="p-3 text-xs text-[#5B6B64]">Esta hoja no tiene modelos rotulados.</p>
                  ) : (
                    pagina.modelos.map((m, i) => (
                      <div key={`${m.modelo}-${i}`} className="border-b border-[#EEF1EF]">
                        <p className="px-2.5 pt-2 text-xs font-bold text-[#16221D]">{m.modelo}</p>
                        {(m.articulos || []).length === 0 ? (
                          <p className="px-2.5 pb-2 text-xs text-[#8A958F]">Sin precio en catálogo</p>
                        ) : (
                          <ul className="divide-y divide-[#EEF1EF]">
                            {m.articulos.map(a => fila({ ...a, aproximado: m.aproximado }, `${i}-`))}
                          </ul>
                        )}
                      </div>
                    ))
                  )
                ) : (pagina.articulos || []).length === 0 ? (
                  <p className="p-3 text-xs text-[#5B6B64]">Esta página no tiene artículos con precio.</p>
                ) : (
                  <ul className="divide-y divide-[#EEF1EF]">
                    {pagina.articulos.map(a => fila(a))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CatalogoFotosAlquiler;
